<template>
    <Teleport to="body">
        <transition name="fade">
            <div
                class="fixed inset-0 flex items-center justify-center bg-slate-800/50 dark:bg-slate-950/80 backdrop-blur-sm"
                :class="{
                    'overflow-y-auto': hasAutoflow,
                    'overflow-hidden': !hasAutoflow,
                }"
                :style="`z-index: ${zIndex}`"
                role="dialog"
                aria-labelledby="modal-title"
                aria-hidden="true"
                v-if="isOpen"
            >
                <div
                    class="w-full relative flex flex-col items-center"
                    :class="{
                        'justify-start': isMobileFullScreen,
                        'justify-center': !isMobileFullScreen,
                        'overflow-y-auto': hasAutoflow,
                        'overflow-hidden': !hasAutoflow,
                    }"
                    :style="{ ...modalStyle, ...maxWidth, ...maxHeight }"
                >
                    <div
                        class="flex-col w-full lg:w-full min-h-28 shadow dark:shadow-slate-900/50 dark:ring-1 dark:ring-slate-700 gap-8 relative bg-white dark:bg-slate-800 p-6"
                        :class="{
                            'flex h-full overflow-y-auto': isMobileFullScreen,
                            'rounded-lg': !isMobileFullScreen,
                            'overflow-y-auto':
                                hasAutoflow && !isMobileFullScreen,
                            'overflow-hidden':
                                !hasAutoflow && !isMobileFullScreen,
                        }"
                        :style="{ width: effectiveModalWidth }"
                    >
                        <div class="pr-10">
                            <div class="flex gap-4 items-center">
                                <div
                                class="w-12 h-12 bg-slate-100 dark:bg-slate-700 rounded-full flex flex-col justify-center items-center"
                                    v-if="icon"
                                >
                                    <Icon
                                        :icon="icon"
                                    path-class="fill-slate-500 dark:fill-slate-400"
                                    />
                                </div>
                                <h5
                                    id="modal-title"
                                class="text-slate-800 dark:text-slate-100 text-xl font-medium"
                                    v-if="title"
                                >
                                    {{ title }}
                                </h5>
                            </div>
                            <button
                                type="button"
                            class="z-10 w-10 rounded-lg h-10 justify-center items-center absolute right-6 top-5 focus:outline-none focus:ring focus:ring-inset focus:ring-slate-200 dark:focus:ring-slate-700"
                                @click="handleClose"
                                tabindex="1"
                            >
                                <Icon
                                    icon="x"
                                path-class="stroke-slate-500 hover:stroke-slate-700 dark:stroke-slate-400 dark:hover:stroke-slate-300"
                                svg-class="fill-slate-500 hover:fill-slate-700 dark:fill-slate-400 dark:hover:fill-slate-300 h-5 w-5"
                                />
                                <span class="sr-only">Close</span>
                            </button>
                        </div>
                        <div
                            :class="{
                                'pt-0': !title,
                                'overflow-hidden': !hasAutoflow,
                                'flex-1': isMobileFullScreen,
                            }"
                        >
                            <slot name="content"></slot>
                        </div>
                        <slot name="errors"></slot>
                        <slot name="footer"></slot>
                    </div>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script setup>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useAppStore } from '@/store/app';
import Icon from '@/components/ui/icon/icon.vue';

const props = defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    title: {
        type: String,
        default: null,
    },
    icon: {
        type: String,
        default: null,
    },
    width: {
        type: String,
        default: '800px',
    },
    hasAutoflow: {
        type: Boolean,
        default: true,
    },
    modalWidth: {
        type: String,
        default: 'min(500px, 95%)', // Default width for smaller modal, won't occupy more than 95% of width
    },
    fullScreenMobile: {
        type: Boolean,
        default: false,
    },
    zIndex: {
        type: Number,
        default: 999,
    },
    modalStyle: {
        type: Object,
        default: () => ({ }),
    },
});

const emit = defineEmits(['close']);

const viewportWidth = ref(0);
const viewportHeight = ref(0);

const appStore = useAppStore();
const { sidebarOpen } = storeToRefs(appStore);

const isMobileFullScreen = computed(
    () =>
        props.fullScreenMobile &&
        viewportWidth.value > 0 &&
        viewportWidth.value < 640
);

const maxWidth = computed(() => {
    if (isMobileFullScreen.value) return {};
    return { maxWidth: `${props.width}` };
});

const maxHeight = computed(() => {
    if (isMobileFullScreen.value)
        return { height: '100dvh', maxHeight: '100dvh' };
    return { maxHeight: '95vh' };
});

const effectiveModalWidth = computed(() => {
    if (isMobileFullScreen.value) return '100%';
    return props.modalWidth;
});

const handleClose = () => {
    emit('close');
};

const updateViewportSize = () => {
    viewportWidth.value = window.innerWidth;
    viewportHeight.value = window.innerHeight;
};

onMounted(() => {
    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);
});

onUnmounted(() => {
    window.removeEventListener('resize', updateViewportSize);
});
</script>
